// Live cross-tab coordination for the SIP phone.
//
// Only one tab holds the phone connection at a time. The owning tab broadcasts
// a `claim`; other tabs record who the owner is (tab id + service-worker client
// id) and, instead of connecting, offer a button to jump to that tab. The owner
// answers `query` (sent by tabs on load) and broadcasts `release` when it
// disconnects.
//
// BroadcastChannel is per-origin and does not deliver a tab its own messages.
// Focusing the owner tab goes through the service worker (see public/sw.js):
// the click's user activation propagates to the SW `message` handler, which is
// allowed to call WindowClient.focus(). Everything is feature-detected and
// degrades to a no-op (the UI then falls back to a normal Connect button).

const CHANNEL_NAME = 'cw-phone';
const TAB_ID =
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const hasBC = typeof BroadcastChannel !== 'undefined';

let channel = null;

const getChannel = () => {
  if (!hasBC) return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
};

export const getPhoneTabId = () => TAB_ID;

// Owner tab: "I hold the phone for this account" (broadcast on connect and in
// response to a query). clientId is this tab's service-worker client id (may be
// null when no service worker controls the page).
export const announcePhoneOwner = (accountId, clientId) =>
  getChannel()?.postMessage({
    type: 'claim',
    accountId,
    from: TAB_ID,
    clientId,
  });

// Owner tab: "I released the phone" (broadcast on disconnect / unload).
export const announcePhoneReleased = accountId =>
  getChannel()?.postMessage({ type: 'release', accountId, from: TAB_ID });

// Any tab on load: "who owns the phone?" — the owner replies with a claim.
export const queryPhoneOwner = accountId =>
  getChannel()?.postMessage({ type: 'query', accountId, from: TAB_ID });

// Subscribe to channel messages. `handler` receives the raw message data.
// Returns an unsubscribe function.
export const subscribePhoneChannel = handler => {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = event => handler(event.data || {});
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
};

const swSupported = () =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

// Resolves the service worker that controls this page, or null. A newly
// registered worker (skipWaiting + clients.claim in public/sw.js) only starts
// controlling shortly after load, so we wait for `controllerchange` once the
// registration is active. Everything is bounded so callers never hang.
const getController = async () => {
  if (!swSupported()) return null;
  const sw = navigator.serviceWorker;
  if (sw.controller) return sw.controller;

  const reg = await Promise.race([
    Promise.resolve(sw.ready).catch(() => null),
    new Promise(resolve => {
      setTimeout(() => resolve(null), 3000);
    }),
  ]);
  if (sw.controller) return sw.controller;
  if (!reg || !reg.active || typeof sw.addEventListener !== 'function') {
    return null;
  }

  return new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve(sw.controller || null);
    };
    const timer = setTimeout(finish, 3000);
    sw.addEventListener(
      'controllerchange',
      () => {
        clearTimeout(timer);
        finish();
      },
      { once: true }
    );
  });
};

// Posts a message to the worker and resolves the reply via `map`, falling back
// to `map(null)` on timeout/error so callers never hang.
const requestFromWorker = (worker, message, map, timeoutMs) =>
  new Promise(resolve => {
    let settled = false;
    const finish = value => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    try {
      const mc = new MessageChannel();
      const timer = setTimeout(() => finish(map(null)), timeoutMs);
      mc.port1.onmessage = ev => {
        clearTimeout(timer);
        finish(map(ev.data));
      };
      worker.postMessage(message, [mc.port2]);
    } catch {
      finish(map(null));
    }
  });

// Round-trips to the service worker to learn this tab's window-client id, so
// other tabs can later ask the SW to focus it. The non-null result is cached;
// null is not, so a later call (once the SW is controlling) can still succeed.
let cachedClientId = null;
export const getOwnClientId = async () => {
  if (cachedClientId) return cachedClientId;
  const controller = await getController();
  if (!controller) return null;
  cachedClientId = await requestFromWorker(
    controller,
    { type: 'WHOAMI' },
    data => data?.clientId ?? null,
    1000
  );
  return cachedClientId;
};

// Focuses the tab that owns the phone via the Chrome extension. The extension
// tracks the connected tab and switches to it with chrome.tabs/windows APIs —
// far more reliable than the service worker's WindowClient.focus(). Resolves
// true only when the extension confirms it focused the tab.
export const focusPhoneTabViaExtension = () =>
  new Promise(resolve => {
    if (typeof window === 'undefined') {
      resolve(false);
      return;
    }
    let settled = false;
    let onResult;
    const finish = value => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onResult);
      resolve(value);
    };
    onResult = event => {
      if (event.data?.type === 'FOCUS_PHONE_TAB_RESULT') {
        finish(Boolean(event.data.focused));
      }
    };
    window.addEventListener('message', onResult);
    setTimeout(() => finish(false), 1500);
    window.postMessage({ type: 'FOCUS_PHONE_TAB' }, '*');
  });

// Asks the service worker to focus the owner tab. Must be called from within a
// user gesture so the activation propagates to the SW. Resolves true only when
// the SW confirms the client was found and focused.
export const focusPhoneTab = async clientId => {
  if (!clientId) return false;
  const controller = await getController();
  if (!controller) return false;
  return requestFromWorker(
    controller,
    { type: 'FOCUS_CLIENT', clientId },
    data => Boolean(data?.focused),
    1500
  );
};
